import dayjs from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en-gb';
import { pluralizeString } from './common';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(advanced);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

const userLocale =
  navigator.language === 'en' || navigator.language.toLowerCase() === 'en-us'
    ? navigator.language
    : 'en-gb';

dayjs.locale(userLocale);

export const getBrowserTimezone = (): string | undefined => {
  try {
    return dayjs.tz.guess() || undefined;
  } catch {
    return undefined;
  }
};

export const getTimeDiffParts = (timeDiffInSec: number) => {
  const totalMinutes = timeDiffInSec / 60;
  const totalHours = totalMinutes / 60;
  const days = totalHours / 24;
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;
  const seconds = timeDiffInSec % 60;

  return {
    days: Math.floor(days),
    hours: Math.floor(hours),
    minutes: Math.floor(minutes),
    seconds: Math.floor(seconds),
  };
};

export const dateCompare = (a: string, b: string) => dayjs(a).diff(b);

const getFormattedTimeParts = (valInSeconds: number) => {
  const { days, hours, minutes, seconds } = getTimeDiffParts(valInSeconds);

  if (days > 0) {
    return [`${days}D`, `${hours}H`, `${minutes}M`, `${seconds}S`];
  }
  if (hours > 0) {
    return [`${hours}H`, `${minutes}M`, `${seconds}S`];
  }
  if (minutes > 0) {
    return [`${minutes}M`, `${seconds}S`];
  }

  return [`${seconds}S`];
};

export const formatTimeValue = (valInSeconds: number, maxParts?: number) => {
  const parts = getFormattedTimeParts(valInSeconds);
  if (maxParts) {
    return parts.slice(0, maxParts).join(' ');
  }
  return parts.join(' ');
};

export const localizeDate = (
  value: string | number | Date | dayjs.Dayjs | null | undefined,
) => dayjs.utc(value).tz();

export const formatRelativeTime = (
  value: string | null | undefined,
  emptyLabel = 'Never',
): string => {
  if (!value) {
    return emptyLabel;
  }
  const date = localizeDate(value);
  return date.isAfter(dayjs()) ? 'Just now' : date.fromNow();
};

// removes year from full date
// 19.09.2024 => 19.09
// 19/09/2024 => 19/09
export const formatShortDate = (
  value: string | number | Date | dayjs.Dayjs | null | undefined,
) => {
  // If it's already a short date string (19.09 or 19/09), return it directly
  if (typeof value === 'string' && /^\d{1,2}[/.]\d{1,2}$/.test(value)) {
    return value;
  }
  return dayjs(value)
    .format('L')
    .replace(/(\d{2})([/.])(\d{2})[/.]\d{4}/, '$1$2$3');
};

export const formatShortDateTime = (
  value: string | number | Date | dayjs.Dayjs | null | undefined,
) => `${formatShortDate(value)} ${dayjs(value).format('LT')}`;

export const timeConsts = {
  second: 1 / 60,
  minute: 1,
  hour: 60,
  day: 1440,
  week: 10080,
  month: 43800,
};

export const formatTimeDuration = (
  timestamp: string | null | undefined,
): string => {
  if (!timestamp) return '-';
  const diffInSeconds = localizeDate(new Date()).diff(
    localizeDate(timestamp),
    'second',
  );
  if (diffInSeconds < 60) return pluralizeString(1, 'minute', 'minutes');
  const { days, hours, minutes } = getTimeDiffParts(diffInSeconds);
  const totalMonths = Math.floor(days / 30);
  const totalYears = Math.floor(days / 365);

  if (totalYears > 0) return pluralizeString(totalYears, 'year', 'years');

  if (totalMonths > 0) return pluralizeString(totalMonths, 'month', 'months');

  if (days > 0) return pluralizeString(days, 'day', 'days');

  if (hours > 0) return pluralizeString(hours, 'hour', 'hours');

  return pluralizeString(minutes, 'minute', 'minutes');
};
